import {
  COMPANY_ADDRESS_FULL,
  COMPANY_LOCATION,
  COMPANY_LONG_SHORT,
  COMPANY_NAME,
  COMPANY_SHORT,
  MAP_URL,
  PROFILE_FACTS,
  PROGRAM_FOCUS,
  PUBLIC_INFO,
  QUICK_NUMBERS,
} from './constants';

function TopBar() {
  return (
    <div className="bg-coal text-cream">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-2 px-5 py-2 text-[11px] font-bold uppercase tracking-[0.22em]">
        <span className="text-brass">Public Profile</span>
        <span className="hidden sm:inline">Nelsonville-York · Athens County · Ohio</span>
        <a href="#address" className="text-cream/80 transition hover:text-brass">
          Mailing Address
        </a>
      </div>
    </div>
  );
}

function Header() {
  const navItems = [
    { label: 'Mission', href: '#mission' },
    { label: 'Programs', href: '#programs' },
    { label: 'Address', href: '#address' },
  ];

  return (
    <header className="sticky top-0 z-30 border-b-4 border-brass bg-paper/95 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-4">
        <a href="#top" className="flex min-w-0 items-center gap-3" aria-label={`${COMPANY_LONG_SHORT} home`}>
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full border-2 border-coal bg-crimson text-sm font-black uppercase tracking-tight text-cream shadow-ribbon">
            {COMPANY_SHORT}
          </span>
          <span className="flex min-w-0 flex-col">
            <span className="truncate text-[11px] font-black uppercase tracking-[0.28em] text-crimson">
              Band Boosters
            </span>
            <span className="truncate text-base font-black tracking-tight text-coal sm:text-lg">
              {COMPANY_LONG_SHORT}
            </span>
          </span>
        </a>
        <nav className="hidden items-center gap-7 text-xs font-black uppercase tracking-[0.22em] text-coal/75 md:flex" aria-label="Primary">
          {navItems.map((item) => (
            <a key={item.href} href={item.href} className="transition hover:text-crimson">
              {item.label}
            </a>
          ))}
        </nav>
        <a
          href={MAP_URL}
          target="_blank"
          rel="noreferrer"
          className="rounded-none border-2 border-coal bg-coal px-4 py-2 text-xs font-black uppercase tracking-[0.2em] text-brass transition hover:bg-crimson hover:border-crimson hover:text-cream"
        >
          Open Map
        </a>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden border-b-4 border-coal bg-coal text-cream">
      <div className="absolute inset-0 bg-coal-stripes opacity-70" aria-hidden="true" />
      <div
        className="absolute -right-32 -top-24 h-[420px] w-[420px] rounded-full bg-crimson/40 blur-3xl"
        aria-hidden="true"
      />
      <div
        className="absolute -left-24 bottom-0 h-[320px] w-[320px] rounded-full bg-brass/25 blur-3xl"
        aria-hidden="true"
      />

      <div className="relative mx-auto grid max-w-7xl gap-12 px-5 py-16 lg:grid-cols-12 lg:py-24">
        <div className="lg:col-span-7">
          <span className="inline-flex items-center gap-2 rounded-full border border-brass/60 bg-coal/60 px-3 py-1 text-[10px] font-black uppercase tracking-[0.32em] text-brass">
            <span className="h-1.5 w-1.5 rounded-full bg-brass" aria-hidden="true" />
            Band Booster Public Profile
          </span>
          <h1 className="mt-6 text-4xl font-black uppercase leading-[1.02] tracking-tight text-cream sm:text-5xl lg:text-6xl">
            {COMPANY_NAME}
          </h1>
          <div className="mt-6 flex items-center gap-3">
            <span className="h-[3px] w-16 bg-brass" aria-hidden="true" />
            <span className="text-xs font-black uppercase tracking-[0.32em] text-brass">
              Nelsonville · Ohio · 45764
            </span>
          </div>
          <p className="mt-7 max-w-2xl text-lg leading-8 text-cream/85">
            A restrained public profile for the booster organization associated with the Nelsonville-York school band
            community. The page presents only the publicly available organization name, mailing address, and city.
          </p>
          <p className="mt-3 max-w-2xl leading-7 text-cream/65">
            Visual language draws from the marching-band tradition — bold uppercase type, brass accents, and the deep
            coal-and-crimson palette of small-town Ohio band uniforms.
          </p>
          <div className="mt-9 flex flex-col gap-3 sm:flex-row">
            <a
              href="#mission"
              className="rounded-none border-2 border-brass bg-brass px-5 py-3 text-center text-sm font-black uppercase tracking-[0.22em] text-coal transition hover:bg-cream hover:border-cream"
            >
              View Mission
            </a>
            <a
              href="#address"
              className="rounded-none border-2 border-cream/40 px-5 py-3 text-center text-sm font-black uppercase tracking-[0.22em] text-cream transition hover:border-crimson hover:bg-crimson"
            >
              Mailing Address
            </a>
          </div>
        </div>

        <aside className="lg:col-span-5">
          <div className="relative h-full min-h-[360px] border-2 border-brass/60 bg-coal/70 p-7 shadow-ribbon">
            <div className="absolute -top-3 left-7 bg-crimson px-3 py-1 text-[10px] font-black uppercase tracking-[0.3em] text-cream">
              At A Glance
            </div>
            <dl className="mt-6 grid gap-5">
              {QUICK_NUMBERS.map((stat) => (
                <div key={stat.label} className="flex items-baseline justify-between gap-4 border-b border-cream/15 pb-4 last:border-0 last:pb-0">
                  <dt className="text-xs font-black uppercase tracking-[0.24em] text-brass">{stat.label}</dt>
                  <dd className="text-right text-2xl font-black uppercase tracking-tight text-cream">{stat.value}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-6 border-t border-cream/15 pt-5">
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-cream/55">Mailing</p>
              <p className="mt-2 text-sm font-bold leading-6 text-cream">{COMPANY_ADDRESS_FULL}</p>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}

function FactsRibbon() {
  return (
    <section aria-label="Profile facts" className="border-b-2 border-coal bg-crimson text-cream">
      <div className="mx-auto grid max-w-7xl divide-y divide-cream/20 px-5 md:grid-cols-3 md:divide-x md:divide-y-0">
        {PROFILE_FACTS.map((fact) => (
          <div key={fact.label} className="py-7 md:px-7 first:md:pl-0 last:md:pr-0">
            <p className="text-[10px] font-black uppercase tracking-[0.32em] text-brass">{fact.label}</p>
            <p className="mt-3 text-lg font-black uppercase tracking-tight text-cream sm:text-xl">{fact.value}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function SectionHeading({ eyebrow, title, children }) {
  return (
    <div className="max-w-2xl">
      <div className="flex items-center gap-3">
        <span className="h-[3px] w-10 bg-crimson" aria-hidden="true" />
        <p className="text-xs font-black uppercase tracking-[0.32em] text-crimson">{eyebrow}</p>
      </div>
      <h2 className="mt-4 text-3xl font-black uppercase tracking-tight text-coal sm:text-4xl">{title}</h2>
      {children ? <p className="mt-5 leading-7 text-muted">{children}</p> : null}
    </div>
  );
}

function MissionSection() {
  return (
    <section id="mission" className="bg-paper">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 py-16 lg:grid-cols-12 lg:py-20">
        <div className="lg:col-span-5">
          <SectionHeading eyebrow="Mission" title="A booster profile for the band community">
            Public sources describe this organization as a booster group connected to the Nelsonville-York school band
            community in Athens County, Ohio.
          </SectionHeading>
        </div>
        <div className="lg:col-span-7">
          <div className="grid gap-5 md:grid-cols-2">
            <div className="border-l-4 border-crimson bg-cream p-6">
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-crimson">Organization</p>
              <p className="mt-3 text-xl font-black uppercase leading-snug tracking-tight text-coal">
                {COMPANY_NAME}
              </p>
            </div>
            <div className="border-l-4 border-brass bg-coal p-6 text-cream">
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-brass">Profile Note</p>
              <p className="mt-3 leading-7 text-cream/85">
                The page keeps the publicly listed mailing address visible while avoiding unsupported operational,
                roster, or fundraising-system details.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ProgramsSection() {
  return (
    <section id="programs" className="border-y-2 border-coal bg-cream">
      <div className="mx-auto max-w-7xl px-5 py-16 lg:py-20">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <SectionHeading eyebrow="Program Focus" title="Conservative public-profile context">
            The layout uses a four-tile grid instead of numbered rows — closer to a booster-program board than a
            corporate marketing page.
          </SectionHeading>
          <p className="max-w-sm text-xs font-black uppercase tracking-[0.28em] text-muted">
            Pride · School Spirit · Community Support
          </p>
        </div>

        <div className="mt-12 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          {PROGRAM_FOCUS.map((note) => (
            <article
              key={note.title}
              className="group relative flex flex-col border-2 border-coal bg-paper p-6 transition hover:-translate-y-0.5 hover:shadow-ribbon"
            >
              <span className="inline-flex w-fit items-center bg-coal px-3 py-1 text-[10px] font-black uppercase tracking-[0.3em] text-brass">
                {note.tag}
              </span>
              <h3 className="mt-5 text-lg font-black uppercase tracking-tight text-coal">{note.title}</h3>
              <p className="mt-3 grow leading-7 text-muted">{note.body}</p>
              <span className="mt-5 block h-[3px] w-10 bg-crimson transition group-hover:w-20" aria-hidden="true" />
            </article>
          ))}
        </div>

        <div className="mt-14 grid gap-4 md:grid-cols-3">
          {PUBLIC_INFO.map((item) => (
            <div key={item.label} className="border-2 border-dashed border-coal/40 bg-paper p-5">
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-crimson">{item.label}</p>
              <p className="mt-2 text-lg font-black uppercase tracking-tight text-coal">{item.value}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AddressBand() {
  return (
    <section id="address" className="relative overflow-hidden bg-coal text-cream">
      <div className="absolute inset-0 bg-coal-stripes opacity-50" aria-hidden="true" />
      <div className="relative mx-auto grid max-w-7xl gap-10 px-5 py-16 lg:grid-cols-12 lg:items-center lg:py-20">
        <div className="lg:col-span-5">
          <span className="text-xs font-black uppercase tracking-[0.32em] text-brass">Mailing Address</span>
          <h2 className="mt-3 text-3xl font-black uppercase tracking-tight sm:text-4xl">{COMPANY_LOCATION}</h2>
          <p className="mt-5 max-w-md leading-7 text-cream/70">
            Use the supplied PO Box for public directory, profile, and correspondence reference.
          </p>
        </div>
        <div className="lg:col-span-7">
          <div className="border-l-4 border-brass bg-cream/5 p-7 backdrop-blur">
            <div className="flex items-center justify-between border-b border-cream/20 pb-4">
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-brass">Organization Address</p>
              <span className="text-[10px] font-black uppercase tracking-[0.3em] text-cream/55">PO Box</span>
            </div>
            <p className="mt-5 text-2xl font-black uppercase leading-snug tracking-tight sm:text-3xl">
              {COMPANY_ADDRESS_FULL}
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <a
                href={MAP_URL}
                target="_blank"
                rel="noreferrer"
                className="rounded-none border-2 border-brass bg-brass px-5 py-3 text-xs font-black uppercase tracking-[0.22em] text-coal transition hover:bg-cream hover:border-cream"
              >
                Open Map
              </a>
              <a
                href="#top"
                className="rounded-none border-2 border-cream/40 px-5 py-3 text-xs font-black uppercase tracking-[0.22em] text-cream transition hover:border-crimson hover:bg-crimson"
              >
                Back To Top
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t-4 border-brass bg-paper text-coal">
      <div className="mx-auto flex max-w-7xl flex-col gap-3 px-5 py-8 text-xs font-bold uppercase tracking-[0.22em] md:flex-row md:items-center md:justify-between">
        <p className="text-coal">
          &copy; {new Date().getFullYear()} {COMPANY_NAME}
        </p>
        <p className="text-muted">{COMPANY_LOCATION}</p>
      </div>
    </footer>
  );
}

function App() {
  return (
    <div id="top" className="min-h-screen bg-paper text-ink">
      <a
        href="#mission"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50 focus:rounded focus:bg-crimson focus:px-3 focus:py-2 focus:text-cream"
      >
        Skip to content
      </a>
      <TopBar />
      <Header />
      <main>
        <Hero />
        <FactsRibbon />
        <MissionSection />
        <ProgramsSection />
        <AddressBand />
      </main>
      <Footer />
    </div>
  );
}

export default App;
